/**
 * Tenant session owner for the DevTools SPA.
 * Google → DevTools member JWT with team_id (membership-gated).
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  authenticateWithGoogleAccessToken,
  DEFAULT_BEATLY_TEAM_ID,
  isNoMemberSessionError,
  isNoTeamMembershipError,
  isUserBlockedError,
  refreshAdminToken,
  requireMemberSession,
  switchTeam as switchTeamRequest,
} from '../services/authApi';
import { trackerApi } from '../services/devtoolsApi';
import type { GoogleUserInfo, UserObject } from '../types';
import {
  clearAuthFromStorage,
  getGoogleUserFromStorage,
  getTokenFromStorage,
  setGoogleUserInStorage,
  updateAuthInStorage,
  userFromAuth,
} from '../utils/tokenManager';

/** Same key as ProjectContext — keep string literal to avoid circular imports. */
const ACTIVE_PROJECT_STORAGE_KEY = 'devtools.activeProjectId';

/** Kitchen visitor JWT user_ids end with `-vis` (minted by admin `/visitor/mint`). */
function isVisitorUserId(userId: string | undefined | null): boolean {
  return Boolean(userId && userId.endsWith('-vis'));
}

/**
 * Kitchen entrance from admin: `/?jwt=…&team_id=…&project=…` (or `/home?jwt=…`).
 * Persist JWT (+ optional project), strip query, and open the console (`/home`) when
 * the link targeted the public landing `/`.
 */
function consumeEntranceQuery(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const jwt = params.get('jwt')?.trim();
    if (!jwt) return null;
    const project = params.get('project')?.trim();
    if (project) {
      try {
        localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, project);
      } catch {
        /* ignore */
      }
    }
    updateAuthInStorage({ token: jwt });
    params.delete('jwt');
    params.delete('team_id');
    params.delete('project');
    const q = params.toString();
    // Marketing `/` must not keep kitchen traffic — send them to the factory home.
    const path = window.location.pathname === '/' ? '/home' : window.location.pathname;
    const cleaned = `${path}${q ? `?${q}` : ''}${window.location.hash}`;
    if (path !== window.location.pathname) {
      window.location.replace(cleaned);
      return jwt;
    }
    window.history.replaceState({}, '', cleaned);
    return jwt;
  } catch {
    return null;
  }
}

/** Attach member team_id / team_ids (or Beatly staff fallback) onto the session user. */
function userWithTeam(
  managerEmail: string,
  teamId: string | undefined,
  teamIds: string[] | undefined,
  googleUser?: GoogleUserInfo | null,
  userId?: string,
  displayName?: string,
): UserObject {
  const resolvedTeam = teamId || DEFAULT_BEATLY_TEAM_ID;
  const ids =
    teamIds && teamIds.length > 0
      ? teamIds
      : [resolvedTeam];
  const base = userFromAuth(managerEmail, googleUser || undefined);
  return {
    ...base,
    ...(userId ? { user_id: userId } : {}),
    ...(displayName ? { name: displayName } : {}),
    team_id: resolvedTeam,
    team_ids: ids,
  };
}

/** Best-effort Google → DevTools profile sync (fill blanks / refresh picture). */
async function syncDevtoolsProfile(user: UserObject): Promise<void> {
  try {
    await trackerApi.putMyProfile({
      displayName: user.name,
      pictureUrl: user.picture,
      fillBlanksOnly: true,
    });
  } catch {
    // Soft-fail: board still works without a profile row.
  }
}

export type AuthStatus = 'loading' | 'unauthenticated' | 'blocked' | 'denied' | 'authenticated';

export interface AuthContextValue {
  user: UserObject | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Kitchen visitor JWT (`role=visitor`) — read-only console session. */
  isVisitor: boolean;
  authStatus: AuthStatus;
  error: string | null;
  blockedUser: GoogleUserInfo | null;
  authenticateWithGoogle: (googleAccessToken: string, googleUser?: GoogleUserInfo) => Promise<boolean>;
  /** Remint JWT for another membership team; clears project-scoped caches via caller. */
  switchTeam: (teamId: string) => Promise<boolean>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Holds the Google → DevTools member JWT session in React state and localStorage. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserObject | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [blockedUser, setBlockedUser] = useState<GoogleUserInfo | null>(null);

  const applySession = useCallback((nextToken: string, nextUser: UserObject) => {
    // Persist first so a refresh mid-render still sees the same JWT.
    updateAuthInStorage({ token: nextToken, user: nextUser });
    setGoogleUserInStorage(nextUser);
    setToken(nextToken);
    setUser(nextUser);
    setAuthStatus('authenticated');
    setBlockedUser(null);
    setError(null);
  }, []);

  /** Drop JWT + profile from memory and localStorage; caller navigates to login. */
  const logout = useCallback(() => {
    clearAuthFromStorage();
    setToken(null);
    setUser(null);
    setBlockedUser(null);
    setError(null);
    setAuthStatus('unauthenticated');
  }, []);

  /** Clear JWT and land on login with authStatus "denied" (no team membership). */
  const denyMembership = useCallback((googleUser?: GoogleUserInfo | null) => {
    clearAuthFromStorage();
    setToken(null);
    setUser(null);
    setAuthStatus('denied');
    setBlockedUser(
      googleUser
        ? { email: googleUser.email, name: googleUser.name, picture: googleUser.picture }
        : null,
    );
    setError(null);
  }, []);

  const authenticateWithGoogle = useCallback(
    async (googleAccessToken: string, googleUser?: GoogleUserInfo) => {
      setIsLoading(true);
      setError(null);
      setAuthStatus('loading');
      try {
        const authData = await authenticateWithGoogleAccessToken(googleAccessToken);
        // Require a member JWT with an active team_id (membership-gated).
        const session = await requireMemberSession(authData.beatly_token);
        const nextUser = userWithTeam(
          authData.manager_email,
          authData.team_id || session.team_id,
          authData.team_ids || session.team_ids,
          googleUser,
          authData.user_id || session.user_id,
        );
        applySession(authData.beatly_token, nextUser);
        // Visitors have no profile row; skip write-on-login sync.
        if (authData.role !== 'visitor') {
          await syncDevtoolsProfile(nextUser);
        }
        return true;
      } catch (authError) {
        clearAuthFromStorage();
        setToken(null);
        setUser(null);
        if (isUserBlockedError(authError)) {
          setAuthStatus('blocked');
          setBlockedUser({
            email: googleUser?.email,
            name: googleUser?.name,
            picture: googleUser?.picture,
          });
          setError(null);
        } else if (isNoTeamMembershipError(authError) || isNoMemberSessionError(authError)) {
          denyMembership(googleUser);
        } else {
          setAuthStatus('unauthenticated');
          setBlockedUser(null);
          setError('auth_failed');
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [applySession, denyMembership],
  );

  useEffect(() => {
    const boot = async () => {
      // 0. Admin kitchen entrance may land with ?jwt=&project= before any storage exists.
      const fromEntrance = consumeEntranceQuery();
      // 1. Empty storage → guest. A blocked/denied verdict from an earlier boot is kept:
      // that boot cleared the JWT, and StrictMode remounts this effect straight after.
      const existing = fromEntrance || getTokenFromStorage();
      if (!existing) {
        setAuthStatus((prev) => (prev === 'blocked' || prev === 'denied' ? prev : 'unauthenticated'));
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setAuthStatus('loading');
      try {
        // 2. Validate the stored member JWT so a stale token cannot keep the shell open
        const refreshed = await refreshAdminToken(existing);
        const session = await requireMemberSession(refreshed.beatly_token);
        applySession(
          refreshed.beatly_token,
          userWithTeam(
            refreshed.manager_email,
            refreshed.team_id || session.team_id,
            refreshed.team_ids || session.team_ids,
            getGoogleUserFromStorage(),
            refreshed.user_id || session.user_id,
            refreshed.display_name || session.name,
          ),
        );
      } catch (refreshError) {
        clearAuthFromStorage();
        setToken(null);
        setUser(null);
        if (isUserBlockedError(refreshError)) {
          setAuthStatus('blocked');
          setBlockedUser(getGoogleUserFromStorage());
        } else if (isNoTeamMembershipError(refreshError) || isNoMemberSessionError(refreshError)) {
          denyMembership(getGoogleUserFromStorage());
        } else {
          setAuthStatus('unauthenticated');
        }
      } finally {
        setIsLoading(false);
      }
    };
    void boot();
  }, [applySession, denyMembership]);

  /** Remint member JWT for another team; ProjectSwitcher catalog refreshes on team_id change. */
  const switchTeam = useCallback(
    async (teamId: string) => {
      if (!token || !teamId || teamId === user?.team_id) return false;
      // Visitor JWTs are single-team and read-only — do not call switch-team.
      if (isVisitorUserId(user?.user_id)) return false;
      setIsLoading(true);
      setError(null);
      try {
        const next = await switchTeamRequest(token, teamId);
        const nextUser = userWithTeam(
          next.manager_email,
          next.team_id,
          next.team_ids,
          getGoogleUserFromStorage(),
          next.user_id,
          next.display_name,
        );
        applySession(next.beatly_token, nextUser);
        // Drop project pick so the new team's catalog re-resolves.
        try {
          localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
          localStorage.removeItem('devtools.selectedBoardId');
        } catch {
          /* ignore */
        }
        return true;
      } catch {
        setError('auth_failed');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [token, user?.team_id, user?.user_id, applySession],
  );

  const isVisitor = Boolean(
    token && user && authStatus === 'authenticated' && isVisitorUserId(user.user_id),
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isLoading,
      isAuthenticated: Boolean(token && user && authStatus === 'authenticated'),
      isVisitor,
      authStatus,
      error,
      blockedUser,
      authenticateWithGoogle,
      switchTeam,
      logout,
    }),
    [
      user,
      token,
      isLoading,
      isVisitor,
      authStatus,
      error,
      blockedUser,
      authenticateWithGoogle,
      switchTeam,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
